import React, { useState, useRef, useEffect, useMemo } from 'react';
import { colors2, icons, UI_BODY_SM, UI_CAPTION_SEMIBOLD, UI_TAG, UI_CAPTION_DF, UI_CAPTION_BOLD_DF } from 'oolib';
import TooltipV2 from '@/components/UI/Tooltip';
import { COMP_ICON_MAP } from '@/utils/getCompIconMap';
import { useAnimatedHeight } from '@/components/analysisModules/reports/ReportLanding/comps/WidgetComps/utils/useAnimatedHeight';
import { SearchableLightbox } from './SearchableLightbox';
import { CascadeFieldPicker } from './CascadeFieldPicker';
import {
  StyledCard,
  StyledInputHeader,
  StyledSlotIndicator,
  StyledFieldSlot,
  StyledFieldName,
  StyledFieldContent,
  StyledActionSlot,
  StyledSideAction,
  StyledTrailingIcon,
  StyledModifierSection,
  StyledModifierCaret,
  StyledModifierLabel,
  StyledModifierRow,
  StyledTooltipSlot,
  StyledTooltipPassthrough,
  StyledStepperButton,
  StyledStepperCenter,
  StyledStepperValueInput,
} from './styled';

const deriveFieldMeta = (value) => {
  if (!value) return { dataType: null, fieldIcon: null };
  const fieldIcon = value.iconName || (value.isAnno ? 'Tag' : COMP_ICON_MAP[value.comp] || null);
  // Meaningful desc (not a component name) takes priority as the top label
  const hasMeaningfulDesc = value.desc && value.desc !== value.comp;
  const dataType = value.dataType || (hasMeaningfulDesc ? value.desc : null) || value.categoryLabel || (value.isAnno ? 'Annotation Tags' : null);
  return { dataType, fieldIcon };
};

/** "Hold Ctrl  See what's happening" hint shown at bottom of NumberInput tooltips */
// const CtrlHintRow = () => (
//   <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.6rem' }}>
//     <span style={{
//       background: colors2.grey80,
//       color: colors2.white,
//       fontSize: 10,
//       fontWeight: 600,
//       padding: '2px 6px',
//       borderRadius: 4,
//       lineHeight: '14px',
//       whiteSpace: 'nowrap',
//     }}>Hold Ctrl</span>
//     <UI_CAPTION_DF style={{ color: colors2.white }}>See what's happening</UI_CAPTION_DF>
//   </div>
// );

/** Animated expand/collapse wrapper for Ctrl-hold extra content */
// const CtrlExpandSection = ({ show, children }) => (
//   <div style={{
//     maxHeight: show ? 200 : 0,
//     opacity: show ? 1 : 0,
//     overflow: 'hidden',
//     transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease-in-out',
//   }}>
//     {children}
//   </div>
// );

/** Wraps children with TooltipV2 if tooltip config is provided.
 *  Always renders a wrapper div so the DOM structure is consistent
 *  whether or not a tooltip is present.
 */
const TooltipWrapper = ({ tooltip, children, ...divProps }) => {
  if (!tooltip) return <div {...divProps}>{children}</div>;
  return (
    <div {...divProps}>
      <TooltipV2
        {...tooltip}
        wrapperDisplay="block"
      >
        {children}
      </TooltipV2>
    </div>
  );
};

/**
 * Find modifier options for the current value.
 * Priority: modifier.options prop > matched option's modifierOptions in options array
 */
const EMPTY_ARRAY = [];
const resolveModifierOptions = (modifier, value, options) => {
  // 1. Direct prop on modifier config
  if (modifier?.options?.length > 0) return modifier.options;

  // 2. Look up from matched option in the options array
  if (!value || !options?.length) return EMPTY_ARRAY;
  const flatOptions = options.flatMap(o => Array.isArray(o.options) ? o.options : [o]);
  const matched = flatOptions.find(o => o.value === value.value);
  return matched?.modifierOptions || EMPTY_ARRAY;
};

/**
 * Internal stepper content rendered inside StyledInputHeader.
 * Handles focus state + draft pattern for editable number input.
 */
const StepperHeaderContent = ({ label, value, min, max, onChange, disabled: stepperDisabled }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef(null);

  const handleInputChange = (e) => setDraft(e.target.value);

  const commitValue = () => {
    setFocused(false);
    const num = parseInt(draft, 10);
    if (!isNaN(num) && num >= min && num <= max) {
      onChange(num);
    } else {
      setDraft(String(value));
    }
  };

  const handleBlur = () => commitValue();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setDraft(String(value));
  };


  return (
    <>
      <StyledStepperButton
        onClick={() => { const n = Math.max(min, value - 1); onChange(n); setDraft(String(n)); }}
        disabled={stepperDisabled || value <= min}
      >
        <icons.CaretLeft size={16} weight="bold" />
      </StyledStepperButton>
      <StyledStepperCenter onClick={() => inputRef.current?.focus()}>
        <UI_TAG style={{ color: colors2.grey60 }}>{label}</UI_TAG>
        <StyledStepperValueInput
          ref={inputRef}
          type="number"
          value={focused ? draft : String(value)}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          min={min}
          max={max}
          disabled={stepperDisabled}
        />
      </StyledStepperCenter>
      <StyledStepperButton
        onClick={() => { const n = Math.min(max, value + 1); onChange(n); setDraft(String(n)); }}
        disabled={stepperDisabled || value >= max}
      >
        <icons.CaretRight size={16} weight="bold" />
      </StyledStepperButton>
    </>
  );
};

export const DataFieldInputSelector = ({
  id,
  options = [],
  indicator: indicatorRaw,
  indicatorHoverIcon = 'List',
  placeholderIcon = 'Plus',
  placeholder = 'Select Input',
  actions = [],
  disabled = false,
  tooltip,
  modifier,
  trailingIcon,
  value = null,
  onChange,
  renderHeader,
  stepper, // { label, value, min, max, onChange, disabled }
  picker, // opt-in: { variant: 'cascade' }. Absent => flat SearchableLightbox.
  hideSearchHeader = false, // flat picker only: hide search + close header (tiny fixed option sets)
  flushRadius, // opt-in, e.g. '11px': row sits flush inside a bordered card — square sides, this bottom radius
  modifierOnly = false, // just the modifier row, no header — a standalone "values shown" control. Renders nothing until `modifier` has options.
}) => {
  // indicator can be string ('A') or object ({ label: 'A', tooltip: {...} })
  const indicatorLabel = typeof indicatorRaw === 'object' ? indicatorRaw?.label : indicatorRaw;
  const indicatorTooltip = typeof indicatorRaw === 'object' ? indicatorRaw?.tooltip : undefined;

  // Resolve modifier options from props (not from value)
  const modifierOptions = useMemo(
    () => resolveModifierOptions(modifier, value, options),
    [modifier?.options, value?.value, options]
  );

  const [showPicker, setShowPicker] = useState(false);
  const [showModifierPicker, setShowModifierPicker] = useState(false);
  const [indicatorHovered, setIndicatorHovered] = useState(false);
  // const [ctrlHeld, setCtrlHeld] = useState(false);
  const [modifierValue, setModifierValue] = useState(() => {
    return value?.modifier || [];
  });
  const cardRef = useRef(null);
  const modifierRef = useRef(null);
  const wrapperStyle = useAnimatedHeight(cardRef);

  // // Track Ctrl key state globally (keydown/keyup)
  // useEffect(() => {
  //   const onKeyDown = (e) => { if (e.key === 'Control') setCtrlHeld(true); };
  //   const onKeyUp = (e) => { if (e.key === 'Control') setCtrlHeld(false); };
  //   const onBlur = () => setCtrlHeld(false);
  //   window.addEventListener('keydown', onKeyDown);
  //   window.addEventListener('keyup', onKeyUp);
  //   window.addEventListener('blur', onBlur);
  //   return () => {
  //     window.removeEventListener('keydown', onKeyDown);
  //     window.removeEventListener('keyup', onKeyUp);
  //     window.removeEventListener('blur', onBlur);
  //   };
  // }, []);

  // Sync internal modifier state when value or modifierOptions change externally
  useEffect(() => {
    if (value?.modifier) {
      setModifierValue(value.modifier);
    } else {
      setModifierValue([]);
    }
  }, [value?.value, value?.modifier, modifierOptions]);

  // Derived
  const { dataType, fieldIcon } = deriveFieldMeta(value);
  const FieldIconComp = fieldIcon ? icons?.[fieldIcon] : null;
  const isClickable = !disabled;
  const IndicatorHoverIconComp = icons?.[indicatorHoverIcon] || icons?.['List'];
  const PlaceholderIconComp = icons?.[placeholderIcon] || icons?.['Plus'];
  const CaretIconComp = icons?.['CaretRight'];
  const TrailingIconComp = trailingIcon ? icons?.[trailingIcon] : null;

  // Modifier config
  const modifierLoading = modifier?.loading || false;
  const modifierDisabled = modifier?.disabled || false;
  const modifierTooltip = modifier?.tooltip;
  const hasModifier = modifier && (modifierOnly || renderHeader || stepper || value) && (modifierOptions.length > 0 || modifierLoading);
  const ModifierIconComp = modifier?.icon ? icons?.[modifier.icon] : icons?.['Eye'];

  // --- Auto-tooltips (internal defaults, prop overrides take priority) ---
  const fieldSlotTooltip = useMemo(() => {
    if (tooltip) return tooltip;
    if (!value) {
      if (indicatorLabel) return { text: 'This input is required for the visualization to work' };
      return { text: 'Click to add data to visualize' };
    }
    const breadcrumb = dataType ? `Dataset: ${dataType} / ${value.display}` : value.display;

    if (value.comp === 'NumberInput') {
      return {
        placement: 'right',
        primaryContent: {
          layout: 'custom',
          render: (
            <div style={{ padding: '0.8rem', maxWidth: '30rem' }}>
              <UI_CAPTION_BOLD_DF style={{ color: 'inherit' }}>Selected Input:</UI_CAPTION_BOLD_DF>
              <UI_CAPTION_BOLD_DF style={{ color: 'inherit' }}>{breadcrumb}</UI_CAPTION_BOLD_DF>
              <UI_CAPTION_DF style={{ color: 'inherit', marginTop: '0.4rem', display: 'block' }}>
                Each number field represents a data series. Add multiple number fields to include them in the calculation. These fields will be combined into a single aggregated series.
              </UI_CAPTION_DF>
              {/* {ctrlHeld && (
                <UI_CAPTION_DF style={{ color: 'inherit', marginTop: '0.4rem', display: 'block', borderTop: `1px solid ${colors2.grey80}`, paddingTop: '0.4rem' }}>
                  Detailed field info: This NumberInput field can be combined with other NumberInput fields using arithmetic expressions (sum, subtract, or custom formulas).
                </UI_CAPTION_DF>
              )}
              {!ctrlHeld && <CtrlHintRow />} */}
            </div>
          ),
        },
      };
    }

    return { text: `Selected Input:\n${breadcrumb}` };
  }, [tooltip, value, indicatorLabel, dataType]);

  const indicatorAutoTooltip = useMemo(() => {
    if (indicatorTooltip) return indicatorTooltip;
    if (!indicatorLabel) return undefined;
    return { text: 'Input slot Indicator' };
  }, [indicatorTooltip, indicatorLabel]);

  const modifierAutoTooltip = useMemo(() => {
    if (modifierTooltip) return modifierTooltip;
    if (!hasModifier) return undefined;
    if (!modifierValue || modifierValue.length === 0) {
      return { text: 'This input is required for the visualization to work' };
    }
    return { text: 'Input modifier' };
  }, [modifierTooltip, hasModifier, modifierValue]);

  const getModifierLabel = () => {
    if (!modifier) return '';
    if (modifierLoading) return 'Loading...';
    if (typeof modifier.label === 'function') {
      return modifier.label(modifierValue, modifierOptions);
    }
    if (typeof modifier.label === 'string') return modifier.label;
    if (!modifierValue || modifierValue.length === 0) return modifier.emptyPlaceholder || 'Select options (required)';
    if (modifierValue.length === modifierOptions.length) return modifier.placeholder || 'All values selected';
    const names = modifierValue.map(v => v.display).join(', ');
    return `${modifierValue.length}/${modifierOptions.length} - ${names}`;
  };

  const emitChange = (selectedOption, newModifierValue) => {
    if (!onChange) return;
    if (!selectedOption) { onChange(id, null); return; }

    // Clean value: only include modifier (selected items), not modifierOptions
    const { modifierOptions: _drop, ...clean } = selectedOption;
    const hasModOpts = resolveModifierOptions(modifier, selectedOption, options).length > 0;
    onChange(id, hasModOpts ? { ...clean, modifier: newModifierValue } : clean);
  };

  const handleFieldSelect = (_, selectedOption) => {
    // Enrich with category label from parent group
    let enriched = selectedOption;
    if (selectedOption && !selectedOption.categoryLabel) {
      const group = options.find(g =>
        Array.isArray(g.options) && g.options.some(o => o.value === selectedOption.value)
      );
      if (group) enriched = { ...selectedOption, categoryLabel: group.display };
    }
    // When a new field is selected, start with no modifier values selected
    setModifierValue([]);
    emitChange(enriched, []);
    setShowPicker(false);
  };

  const handleModifierToggle = (_, toggledItem) => {
    const exists = modifierValue.some(v => v.value === toggledItem.value);
    const newSelected = exists
      ? modifierValue.filter(v => v.value !== toggledItem.value)
      : [...modifierValue, toggledItem];
    setModifierValue(newSelected);
    emitChange(value, newSelected);
  };

  // Modifier-only has nothing to show until the modifier has options; an empty
  // card would still claim its flex gap in the parent.
  if (modifierOnly && !hasModifier) return null;

  return (
    <div style={wrapperStyle}>
      <div ref={cardRef}>
      <StyledCard>
        {modifierOnly ? null : renderHeader ? renderHeader() : (
        <StyledInputHeader
          $clickable={stepper ? false : isClickable}
          $disabled={stepper?.disabled || disabled}
          $hasModifier={hasModifier}
          $active={showPicker}
        >
          {stepper ? (
            <StepperHeaderContent {...stepper} />
          ) : (
          <>

          {indicatorLabel && (
            <StyledTooltipPassthrough>
              <TooltipWrapper tooltip={indicatorAutoTooltip}>
                <StyledSlotIndicator
                  onMouseEnter={() => setIndicatorHovered(true)}
                  onMouseLeave={() => setIndicatorHovered(false)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: 14, justifyContent: 'center', height: '100%' }}>
                    {indicatorHovered
                      ? (IndicatorHoverIconComp ? <IndicatorHoverIconComp size={14} color="currentColor" /> : null)
                      : <UI_TAG style={{ color: 'currentColor', fontWeight: 600 }}>{indicatorLabel}</UI_TAG>
                    }
                  </div>
                </StyledSlotIndicator>
              </TooltipWrapper>
            </StyledTooltipPassthrough>
          )}

          <StyledTooltipSlot>
            <TooltipWrapper tooltip={fieldSlotTooltip}>
              <StyledFieldSlot
                $clickable={isClickable}
                $disabled={disabled}
                $isFirst={!indicatorLabel}
                $isLast={actions.length === 0}
                onClick={isClickable ? () => setShowPicker(true) : undefined}
              >
                {value ? (
                  <>
                    <StyledFieldContent $hasIndicator={!!indicatorLabel}>
                      {dataType && (
                        <UI_TAG style={{ color: colors2.grey50, wordWrap: 'break-word', marginBottom: '0.2rem' }}>{dataType}</UI_TAG>
                      )}
                      <StyledFieldName>
                        {FieldIconComp && <FieldIconComp size={14} weight="bold" style={{ flexShrink: 0 }} {...(fieldIcon === 'RadioButton' ? { weight: 'fill' } : {})} />}
                        <UI_BODY_SM style={{ color: colors2.grey80 }}>
                          {value.display}
                        </UI_BODY_SM>
                      </StyledFieldName>
                    </StyledFieldContent>
                    {TrailingIconComp && <StyledTrailingIcon><TrailingIconComp size={14} color="currentColor" weight="bold" /></StyledTrailingIcon>}
                  </>
                ) : (
                  <StyledFieldContent $hasIndicator={!!indicatorLabel} $isPlaceholder>
                    {PlaceholderIconComp && <PlaceholderIconComp size={14} color={colors2.grey80} />}
                    <UI_BODY_SM style={{ color: colors2.grey50 }}>{placeholder}</UI_BODY_SM>
                  </StyledFieldContent>
                )}
              </StyledFieldSlot>
            </TooltipWrapper>
          </StyledTooltipSlot>

          <StyledActionSlot>
            {actions.map((action, i) => {
              const ActionIcon = icons?.[action.icon];
              if (!ActionIcon) return null;
              return (
                <StyledTooltipPassthrough key={i}>
                  <TooltipWrapper tooltip={action.tooltip || { text: 'Remove Input' }}>
                    <StyledSideAction
                      $hoverColor={action.hoverColor}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (action.opensLightbox) setShowPicker(true);
                        action.onClick?.();
                      }}
                    >
                      <ActionIcon size={14} color="currentColor" weight="bold" />
                    </StyledSideAction>
                  </TooltipWrapper>
                </StyledTooltipPassthrough>
              );
            })}
          </StyledActionSlot>
          </>
          )}
        </StyledInputHeader>
        )}

        {hasModifier && (
          <TooltipWrapper tooltip={modifierAutoTooltip}>
            <StyledModifierSection ref={modifierRef} $standalone={modifierOnly} $flushRadius={flushRadius} $disabled={modifierDisabled} $loading={modifierLoading} $active={showModifierPicker}>
              <StyledModifierRow onClick={(modifierDisabled || modifierLoading) ? undefined : (e) => { e.stopPropagation(); setShowModifierPicker(true); }}>
                {ModifierIconComp && <ModifierIconComp size={14} weight="bold" />}
                <StyledModifierLabel>
                  <UI_CAPTION_SEMIBOLD>{getModifierLabel()}</UI_CAPTION_SEMIBOLD>
                </StyledModifierLabel>
                {!modifierDisabled && CaretIconComp && <StyledModifierCaret><CaretIconComp size={12} color="currentColor" weight='bold' /></StyledModifierCaret>}
              </StyledModifierRow>
            </StyledModifierSection>
          </TooltipWrapper>
        )}
      </StyledCard>

      {showPicker && (
        picker?.variant === 'cascade' ? (
          <CascadeFieldPicker
            anchorEl={cardRef.current}
            open
            onOpenChange={(v) => { if (!v) setShowPicker(false); }}
            options={options}
            value={value}
            // Route through the SAME handleFieldSelect the flat picker uses, so
            // categoryLabel enrichment + emitChange + close are byte-identical.
            onChange={(opt) => handleFieldSelect(null, opt)}
          />
        ) : (
          <SearchableLightbox
            anchorRef={cardRef}
            onClose={() => setShowPicker(false)}
            options={options}
            value={value}
            onChange={handleFieldSelect}
            grouped={options.some(o => Array.isArray(o.options))}
            width={300}
            hideSearchHeader={hideSearchHeader}
          />
        )
      )}

      {showModifierPicker && (
        <SearchableLightbox
          anchorRef={modifierRef}
          onClose={() => setShowModifierPicker(false)}
          options={modifierOptions}
          value={modifierValue}
          onChange={handleModifierToggle}
          isMulti
          width={modifier?.lightboxWidth || 280}
          searchPlaceholder={modifier?.searchPlaceholder || 'Search'}
          renderContent={modifier?.renderLightbox}
        />
      )}
    </div>
    </div>
  );
};

export default DataFieldInputSelector;
